/**
 * Libellés des sources auxquelles une note peut se rattacher.
 *
 * Le champ `platform` d'une note reçoit le `type` du compte choisi. Il ne couvre pas que les
 * régies publicitaires : une note documente aussi bien un incident de tracking ou une reprise
 * de tagging sur une propriété analytique.
 *
 * Les écrans des notes ne connaissaient que trois régies — Google, Microsoft, Meta — et
 * affichaient le reste en clé brute : « PINTEREST_ADS » au lieu de « Pinterest Ads ». Cette
 * table les couvre toutes.
 *
 * Elle est encore recopiée ailleurs (`kanban/KanbanView.jsx`, `StatisticsView.jsx`,
 * `ConsolidatedDashboardView.jsx`, `kanban/modals/ImportAccountsModal.jsx`) : ces copies
 * servent d'autres écrans et n'ont pas été touchées ici.
 */

export const PLATEFORMES = {
  GOOGLE_ADS: 'Google Ads',
  MICROSOFT_ADS: 'Microsoft Ads',
  FACEBOOK_ADS: 'Meta Ads',
  PINTEREST_ADS: 'Pinterest Ads',
  TIKTOK_ADS: 'TikTok Ads',
  OPENAI_ADS: 'ChatGPT Ads',
  AWIN: 'Awin',
  GOOGLE_ANALYTICS: 'Google Analytics',
  PIANO_ANALYTICS: 'Piano Analytics',
  MATOMO: 'Matomo',
  GOOGLE_MERCHANT_API: 'Google Merchant Center',
  GOOGLE_SEARCH_CONSOLE: 'Google Search Console',
  // Même nom de part et d'autre — `LibraryItem.type` comme `ApiProvider` —, d'où l'absence
  // de doublon dans le bloc ci-dessous : c'est délibéré, et la quatrième divergence de
  // nomenclature que le dépôt n'a pas créée.
  PRESTASHOP: 'PrestaShop',
  // Même nom de part et d'autre lui aussi : le type du `LibraryItem`, le `provider` de la
  // connexion et la valeur d'`ApiProvider` coïncident, d'où l'absence de doublon plus bas.
  GOOGLE_TAG_MANAGER: 'Google Tag Manager',

  /*
   * Les clés de l'énumération `ApiProvider`, qui journalise les appels.
   *
   * Elle ne nomme pas les plateformes comme `LibraryItem.type` : Meta y est `META_ADS` et non
   * `FACEBOOK_ADS`, Merchant `GOOGLE_MERCHANT_CENTER` et non `GOOGLE_MERCHANT_API`. Les deux
   * nomenclatures coexistent en base ; les réconcilier demanderait une migration de données que
   * rien ne justifie. Les deux jeux de clés rendent donc le même libellé, et l'écran des erreurs
   * affiche « Meta Ads » qu'il lise l'un ou l'autre.
   */
  META_ADS: 'Meta Ads',
  GOOGLE_MERCHANT_CENTER: 'Google Merchant Center',
  GOOGLE_ANALYTICS_4: 'Google Analytics',
  MCP: 'MCP'
};

/**
 * Les CMS e-commerce, et l'écran de chacun.
 *
 * ## Pourquoi une famille à part, et non une entrée de plus sous « Analytics »
 *
 * Une boutique n'est pas une solution de mesure. Elle ne compte ni session, ni visiteur, ni
 * canal : elle sait ce qui a été **encaissé**. Les ranger ensemble obligerait le lecteur à
 * deviner, dans une même liste, lesquelles répondent « combien de gens sont venus » et
 * lesquelles répondent « combien ont acheté ».
 *
 * ⚠️ C'est aussi ce qui empêche une boutique de se faire passer pour une solution de mesure
 * ailleurs dans l'application — `destinationsDuChoix` proposait le dashboard consolidé à tout ce
 * qui n'était pas une régie, donc à une boutique, et la carte menait à un écran vide.
 *
 * ## Ajouter Shopify, WooCommerce, Magento
 *
 * Une entrée ici, un logo dans `App.js`, une liste de comptes filtrés, et la famille apparaît
 * d'elle-même dans la barre latérale **et** dans l'écran de choix : les deux parcourent cette
 * liste au lieu d'énumérer les CMS un par un. Le reste — connecteur, route, vue — est le travail
 * propre à chaque CMS.
 */
export const CMS = [
  { cle: 'PRESTASHOP', libelle: 'PrestaShop', vue: 'prestashop-dashboard' }
];

/** Les clés des CMS, pour les tests d'appartenance. */
export const EST_UN_CMS = (type) => CMS.some(c => c.cle === type);

/** Libellé d'une plateforme. Une clé inconnue est rendue telle quelle plutôt que masquée. */
export const libellePlateforme = (cle) => (cle ? (PLATEFORMES[cle] || cle) : null);

/** Options de filtre, dans l'ordre : régies d'abord, solutions de mesure ensuite. */
export const OPTIONS_PLATEFORMES = Object.entries(PLATEFORMES).map(([value, label]) => ({ value, label }));

/**
 * Comptes regroupés par plateforme, pour les `<optgroup>` d'une liste déroulante.
 *
 * Passé quelques dizaines de comptes, une liste à plat oblige à lire chaque ligne pour
 * trouver la bonne : les libellés se ressemblent d'une régie à l'autre, et rien ne dit à
 * quelle plateforme appartient « BZB ».
 *
 * Groupes et comptes sont triés par ordre alphabétique, avec `localeCompare` — un tri brut
 * placerait « Élan » après « Zèbre ».
 *
 * Un logo dans l'option serait plus lisible encore, mais un `<select>` natif ne rend que du
 * texte : il faudrait un composant sur mesure.
 */
export const grouperComptes = (comptes = []) => {
  const groupes = new Map();
  for (const compte of comptes) {
    const cle = compte.type || 'AUTRE';
    if (!groupes.has(cle)) groupes.set(cle, []);
    groupes.get(cle).push(compte);
  }

  return [...groupes.entries()]
    .map(([type, liste]) => ({
      type,
      libelle: libellePlateforme(type),
      comptes: [...liste].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
    }))
    .sort((a, b) => a.libelle.localeCompare(b.libelle));
};

/**
 * Le dashboard qui ouvre un compte, selon son type.
 *
 * Cette correspondance existait en clair dans `HomePage`, sous forme d'un `switch` : la cloche
 * d'alertes en avait besoin à son tour, et une seconde copie aurait divergé au premier onglet
 * ajouté — le jour où Awin est arrivé, l'un des deux l'aurait ignoré et ouvert Google Ads à sa
 * place, ce qui est précisément le `default` de ce genre de `switch`.
 *
 * **Les types absents n'ont pas de dashboard à ouvrir** : une propriété Search Console ou un flux
 * Merchant Center ne se consulte pas ici. L'absence vaut réponse — l'appelant n'a pas à deviner,
 * il regarde si la clé existe, et s'abstient de proposer un lien qui ne mènerait nulle part.
 */
export const DASHBOARD_PAR_TYPE = {
  GOOGLE_ADS: 'google',
  MICROSOFT_ADS: 'microsoft',
  FACEBOOK_ADS: 'facebook-ads',
  PINTEREST_ADS: 'pinterest-ads',
  TIKTOK_ADS: 'tiktok-ads',
  OPENAI_ADS: 'chatgpt-ads',
  AWIN: 'awin'
};

/**
 * Les **vues** où se consulte une solution de mesure, par type de compte.
 *
 * Une table à part, et non trois entrées de plus dans `DASHBOARD_PAR_TYPE` : celle-ci nomme des
 * *onglets* du tableau de bord des régies, celle-là des *vues* à part entière de l'application.
 * Les mélanger ferait passer `ga4-dashboard` pour un onglet, et la cloche d'alertes — qui ne
 * concerne que les dépassements de budget publicitaire — se mettrait à proposer des liens vers
 * des écrans analytiques où elle n'a rien à dire.
 *
 * Elle est **composée** à partir de la première, pour qu'un onglet ajouté là arrive ici sans
 * qu'on ait à y penser. L'ordre compte : les vues l'emportent sur les onglets de même clé, ce
 * qui n'arrive pas aujourd'hui et resterait de toute façon le bon choix.
 */
export const ECRAN_PAR_TYPE = {
  ...DASHBOARD_PAR_TYPE,
  GOOGLE_ANALYTICS: 'ga4-dashboard',
  MATOMO: 'matomo-dashboard',
  MATOMO_OAUTH: 'matomo-dashboard',
  PIANO_ANALYTICS: 'piano-dashboard',
  // La boutique a sa vue, au même titre que les trois solutions de mesure.
  PRESTASHOP: 'prestashop-dashboard'
};

/** Vrai si ce type se consulte dans une vue à part, et non dans un onglet de régie. */
export function estVueAutonome(type) {
  return Boolean(ECRAN_PAR_TYPE[type]) && !DASHBOARD_PAR_TYPE[type];
}

/**
 * Les dashboards de plateforme, un par écran — le catalogue des droits d'accès client.
 *
 * ⚠️ **Dérivé d'`ECRAN_PAR_TYPE`, jamais écrit à la main.** C'est tout l'intérêt : un connecteur
 * ajouté là arrive ici sans qu'on y pense, donc son dashboard devient accordable ou retirable
 * le jour où il devient consultable. Une liste recopiée aurait oublié le connecteur suivant, et
 * l'oubli serait muet — le dashboard resterait simplement toujours ouvert.
 *
 * ⚠️ **Indexé par écran, et non par type** : `MATOMO` et `MATOMO_OAUTH` désignent le même
 * `matomo-dashboard`, et deux interrupteurs pour un seul écran seraient deux réglages
 * contradictoires pour un seul effet. Les types qui partagent un écran sont donc regroupés, et
 * `typesDuDashboard` les rend tous — c'est par eux qu'on sait si le client a de quoi l'ouvrir.
 *
 * ⚠️ **Le dashboard consolidé n'en fait pas partie**, et c'est voulu : il est déjà un droit à
 * lui seul (`consolidated-ga4`), avec son propre verdict d'usage. L'ajouter ici donnerait deux
 * réglages pour une même chose, dont l'un contredirait l'autre.
 */
export const DASHBOARDS_DE_PLATEFORME = Object.entries(ECRAN_PAR_TYPE)
  .reduce((acc, [type, ecran]) => {
    const existant = acc.find(d => d.id === ecran);
    if (existant) { existant.types.push(type); return acc; }
    acc.push({ id: ecran, libelle: libellePlateforme(type), types: [type] });
    return acc;
  }, []);

/** Les identifiants des dashboards de plateforme, dans l'ordre du catalogue. */
export const IDS_DASHBOARDS_DE_PLATEFORME = DASHBOARDS_DE_PLATEFORME.map(d => d.id);

/** Les types de compte qui ouvrent ce dashboard — plusieurs quand ils partagent un écran. */
export const typesDuDashboard = (id) => (
  DASHBOARDS_DE_PLATEFORME.find(d => d.id === id)?.types || []
);

/** Le dashboard de plateforme d'un écran, ou `undefined` s'il n'en est pas un. */
export const dashboardDePlateforme = (id) => DASHBOARDS_DE_PLATEFORME.find(d => d.id === id);
